import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import JitsiJoinModal from './JitsiJoinModal';
import { getJitsiRoomName, getPreferredJoinURL } from '../utils/jitsiHelper';
import './TeacherOnlineClassesView.css';

function TeacherOnlineClassesView({ teacherId, schoolId }) {
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jitsiModal, setJitsiModal] = useState({ isOpen: false, roomName: '', displayName: '' });

  const loadOnlineClasses = useCallback(async () => {
    try {
      setLoading(true);
      setOnlineClasses([]);

      // Fetch all classes for this teacher (scoped to school if provided)
      const classesRef = collection(db, 'classes');
      const classesQuery = schoolId
        ? query(classesRef, where('teacherId', '==', teacherId), where('schoolId', '==', schoolId))
        : query(classesRef, where('teacherId', '==', teacherId));
      const classesSnapshot = await getDocs(classesQuery);
      const classIds = classesSnapshot.docs.map(doc => doc.id);

      if (classIds.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch online classes for each teacher's class
      const allOnlineClasses = [];
      for (const classId of classIds) {
        const onlineClassesRef = collection(db, 'classes', classId, 'onlineClasses');
        const snapshot = await getDocs(onlineClassesRef);
        const classes = snapshot.docs.map(doc => ({
          id: doc.id,
          classId: classId,
          ...doc.data(),
        }));
        allOnlineClasses.push(...classes);
      }

      // Sort by date (newest first)
      allOnlineClasses.sort((a, b) => new Date(b.date) - new Date(a.date));
      setOnlineClasses(allOnlineClasses);
    } catch (error) {
      console.error('Error loading online classes:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherId, schoolId]);

  useEffect(() => {
    loadOnlineClasses();
  }, [loadOnlineClasses]);

  const handleJoinClass = (onlineClass) => {
    const joinURL = getPreferredJoinURL(onlineClass);
    if (!joinURL) {
      alert('This class is not joinable at this time.');
      return;
    }

    if (!joinURL.includes('meet.jit.si')) {
      window.open(joinURL, '_blank');
      return;
    }

    const roomName = getJitsiRoomName(onlineClass.id);
    const displayName = onlineClass.teacherName || 'Teacher';
    setJitsiModal({ isOpen: true, roomName, displayName });
  };

  const closeJitsiModal = () => {
    setJitsiModal({ isOpen: false, roomName: '', displayName: '' });
  };

  const handleEndClass = async (onlineClass) => {
    if (!window.confirm('Are you sure you want to end this class? This will mark it as completed.')) return;
    try {
      const classDocRef = doc(db, 'classes', onlineClass.classId, 'onlineClasses', onlineClass.id);
      const endedAtISO = new Date().toISOString();
      await updateDoc(classDocRef, {
        status: 'Completed',
        // iOS OnlineClass decodes `completedAt`; web used `endedAt` — write both.
        completedAt: endedAtISO,
        endedAt: endedAtISO,
      });
      loadOnlineClasses();
    } catch (error) {
      console.error('Error ending class:', error);
      alert('Failed to end class: ' + error.message);
    }
  };

  const canJoin = (onlineClass) => {
    const isOnline = onlineClass.classMode === 'Online' || onlineClass.classMode === 'Hybrid';
    const isActive = onlineClass.status === 'Active' || onlineClass.status === 'active';
    const now = new Date();
    const classDate = new Date(onlineClass.date);
    const isToday = classDate.toDateString() === now.toDateString();
    return isOnline && (isActive || isToday) && getPreferredJoinURL(onlineClass);
  };

  const upcomingClasses = onlineClasses.filter(
    c => new Date(c.date) >= new Date() && c.status !== 'Cancelled' && c.status !== 'cancelled'
  );
  const activeClasses = onlineClasses.filter(
    c => c.status === 'Active' || c.status === 'active'
  );
  const previousClasses = onlineClasses.filter(
    c => c.status === 'Completed' || c.status === 'completed' || new Date(c.date) < new Date()
  );

  return (
    <div className="teacher-online-classes-view">
      <h2>Online Classes</h2>

      {loading ? (
        <div className="loading">Loading online classes...</div>
      ) : onlineClasses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📹</div>
          <h3>No Online Classes</h3>
          <p>Schedule online classes for your students to see them here.</p>
        </div>
      ) : (
        <div className="online-classes-container">
          {activeClasses.length > 0 && (
            <section className="classes-section active">
              <h2>Active Now</h2>
              <div className="classes-list">
                {activeClasses.map(onlineClass => (
                  <div key={onlineClass.id} className="class-card active">
                    <div className="class-header">
                      <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                        {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                      </span>
                      <span className="class-status active">Active</span>
                    </div>
                    <h3>{onlineClass.subjectName || 'Class'}</h3>
                    <p className="topic">{onlineClass.topic || 'No topic'}</p>
                    <div className="class-meta">
                      <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                      <span>⏰ {onlineClass.startTime} - {onlineClass.endTime}</span>
                    </div>
                    <div className="class-actions">
                      {canJoin(onlineClass) && (
                        <button className="join-button" onClick={() => handleJoinClass(onlineClass)}>
                          Join Class
                        </button>
                      )}
                      <button className="end-button" onClick={() => handleEndClass(onlineClass)}>
                        End Class
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="classes-section">
            <h2>Upcoming Classes</h2>
            {upcomingClasses.length === 0 ? (
              <p className="no-classes">No upcoming classes</p>
            ) : (
              <div className="classes-list">
                {upcomingClasses.map(onlineClass => (
                  <div key={onlineClass.id} className="class-card">
                    <div className="class-header">
                      <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                        {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                      </span>
                      <span className={`class-status ${onlineClass.status?.toLowerCase()}`}>
                        {onlineClass.status}
                      </span>
                    </div>
                    <h3>{onlineClass.subjectName || 'Class'}</h3>
                    <p className="topic">{onlineClass.topic || 'No topic'}</p>
                    <div className="class-meta">
                      <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                      <span>⏰ {onlineClass.startTime} - {onlineClass.endTime}</span>
                    </div>
                    {canJoin(onlineClass) && (
                      <button className="join-button" onClick={() => handleJoinClass(onlineClass)}>
                        Join Class
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {previousClasses.length > 0 && (
            <section className="classes-section">
              <h2>Previous Classes</h2>
              <div className="classes-list">
                {previousClasses.slice(0, 10).map(onlineClass => (
                  <div key={onlineClass.id} className="class-card previous">
                    <div className="class-header">
                      <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                        {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                      </span>
                      <span className={`class-status ${onlineClass.status?.toLowerCase()}`}>
                        {onlineClass.status}
                      </span>
                    </div>
                    <h3>{onlineClass.subjectName || 'Class'}</h3>
                    <p className="topic">{onlineClass.topic || 'No topic'}</p>
                    <div className="class-meta">
                      <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <JitsiJoinModal
        isOpen={jitsiModal.isOpen}
        roomName={jitsiModal.roomName}
        displayName={jitsiModal.displayName}
        onClose={closeJitsiModal}
      />
    </div>
  );
}

export default TeacherOnlineClassesView;
